
/* 
  Developed by: Jordyn Kao
  Contact: jordyninternational@gmail.com
  This component is part of the application and contains internal credits.
  This code is protected under the terms of the contract agreement.
  Unauthorized use or distribution of this code is prohibited.
*/
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUp, Shield, Users, ArrowRight, BarChart2, Home, DollarSign } from "lucide-react";
import { Button, LinkButton } from '@/components/Button';
import { TitleCaseSpan } from './TitleCase';
import { useState, useEffect } from 'react';

const PortfolioSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );
    
    const element = document.querySelector('#portfolio-section');
    if (element) observer.observe(element);
    
    return () => observer.disconnect();
  }, []);

  const portfolioItems = [
    {
      title: "South African real estate",
      description: "Access commercial and residential property opportunities across major SA cities with strong growth potential and stable returns.",
      image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&h=600&fit=crop",
      icon: Home,
      return: "8-12%",
      stats: [
        { label: "Target return", value: "8-12%" },
        { label: "Investment term", value: "5-7 years" },
        { label: "Minimum investment", value: "R50,000" }
      ],
      chartData: [65, 59, 80, 81, 56, 55, 40],
      chartLabels: ['2023', '2024', '2025', '2026', '2027', '2028', '2029']
    },
    {
      title: "Private credit & debt",
      description: "Diversified lending strategies across emerging market opportunities with secured assets and predictable income streams.",
      image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&h=600&fit=crop",
      icon: DollarSign,
      return: "10-15%",
      stats: [
        { label: "Target return", value: "10-15%" },
        { label: "Investment term", value: "3-5 years" },
        { label: "Minimum investment", value: "R100,000" }
      ],
      chartData: [30, 45, 60, 72, 85, 92, 100],
      chartLabels: ['2023', '2024', '2025', '2026', '2027', '2028', '2029']
    },
    {
      title: "Alternative assets",
      description: "Curated opportunities in commodities, private equity, and specialty finance for enhanced portfolio diversification.",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=600&fit=crop",
      icon: BarChart2,
      return: "12-18%",
      stats: [
        { label: "Target return", value: "12-18%" },
        { label: "Investment term", value: "7-10 years" },
        { label: "Minimum investment", value: "R250,000" }
      ],
      chartData: [20, 35, 55, 75, 85, 95, 110],
      chartLabels: ['2023', '2024', '2025', '2026', '2027', '2028', '2029']
    }
  ];

  const currentItem = portfolioItems[activeTab];

  return (
        <section id="portfolio-section" className="py-20 md:py-28 bg-slate-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
                    <h2 className="text-4xl font-bold text-gray-900 mb-4">
            A Glimpse Into Our Portfolio
          </h2>
                    <p className="text-lg text-slate-600 max-w-3xl mx-auto">
            Explore a selection of our live and past deals to understand our investment approach and the types of opportunities we offer.
          </p>
        </motion.div>

        {/* Tab Navigation */}
                <motion.div 
          className="flex justify-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.2, duration: 0.8 }}
        >
          <div className="bg-slate-200/60 p-1.5 rounded-lg sm:rounded-full flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            {portfolioItems.map((item, index) => (
              <Button
                key={index}
                onClick={() => setActiveTab(index)}
                variant={activeTab === index ? 'ghost' : 'solid'}
                colorScheme="primary"
                className={`w-full text-center sm:w-auto px-5 py-2.5 rounded-md sm:rounded-full text-sm font-semibold transition-colors relative ${activeTab === index ? 'text-[hsl(var(--navy))]' : ''}`}>
                {activeTab === index && (
                  <motion.div
                    layoutId="active-portfolio-pill"
                    className="absolute inset-0 bg-white rounded-full shadow-md"
                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                  />
                )}
                <span className="relative z-10"><TitleCaseSpan>{item.title}</TitleCaseSpan></span>
              </Button>
            ))}
          </div>
        </motion.div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
                    <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-1 lg:grid-cols-5 gap-x-12 gap-y-8 items-start"
          >
            <div className="lg:col-span-3 space-y-8 bg-white p-8 rounded-2xl shadow-xl">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                  <currentItem.icon className="w-6 h-6 text-[hsl(var(--navy))]" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900"><TitleCaseSpan>{currentItem.title}</TitleCaseSpan></h3>
              </div>
              
              <p className="text-lg text-slate-600 leading-relaxed">{currentItem.description}</p>
              
              <div className="grid sm:grid-cols-3 gap-4 pt-4">
                {currentItem.stats.map((stat, index) => (
                  <div key={index} className="bg-[hsl(var(--navy))] p-4 rounded-xl shadow-md transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5">
                    <p className="text-sm text-slate-300">{stat.label}</p>
                    <p className="text-xl font-semibold text-white">{stat.value}</p>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="lg:col-span-2 bg-gradient-to-br from-[hsl(var(--navy))] to-[hsl(var(--navy-dark))] rounded-2xl p-8 text-white shadow-2xl flex flex-col h-full">
              <div className="flex-grow flex flex-col items-center justify-center text-center">
                <p className="text-slate-300 text-lg mb-2">Projected Annual Return</p>
                <div className="text-6xl font-bold text-white mb-6">{currentItem.return}</div>
                <div className="mt-8">
                  <LinkButton 
                    to="/investments"
                    variant="solid"
                    colorScheme="inverted"
                    className="group w-full sm:w-auto"
                  >
                    <span>View Deal</span>
                    <ArrowRight className="w-4 h-4 ml-2 transition-transform duration-300 group-hover:translate-x-1" />
                  </LinkButton>
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        <motion.div 
          className="text-center mt-16"
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6, duration: 0.8 }}
        >
          <a 
            href="https://app.alta-x.com/register"
            className="inline-flex items-center group rounded-full px-8 py-4 text-lg font-semibold bg-[hsl(var(--navy))] text-white hover:bg-[hsl(var(--navy-dark))] transition-colors"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="mr-2">Start Investing with ALTA-X</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </a>
          
          <p className="mt-4 text-sm text-gray-500">
            Already have an account?{' '}
            <a href="https://app.alta-x.com/login" className="text-[hsl(var(--navy))] hover:underline">
              Sign in
            </a>
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default PortfolioSection;
